import { type Node as ProseMirrorNode } from '@tiptap/pm/model';
import { Plugin, PluginKey } from '@tiptap/pm/state';
import { Decoration, DecorationSet } from '@tiptap/pm/view';
import { type Editor } from '@tiptap/react';

import { SELECTION_CONTEXT_HIGHLIGHT_CLASS } from '@/components/tiptap/shared/selection-context-highlight';
import { isEditorLive } from '@heidi/editor/core';
/**
 * Decoration layer for inline selection edits (selection → Ask Heidi).
 *
 * - `generating`: paints a pending/shimmer treatment over the range while the
 *   AI edit streams (the document is untouched).
 * - `review-highlight`: keeps the original steadily highlighted while the
 *   proposed text or key points render in the review panel.
 */
export const inlineSelectionEditPluginKey = new PluginKey<InlineEditPluginState>(
  'inlineSelectionEdit'
);

/** Class defined in `src/styles/globals.css` (gentle pulse, reduced-motion safe). */
export const INLINE_EDIT_PENDING_CLASS = 'inline-edit-pending';

export interface InlineEditRange {
  from: number;
  to: number;
}

export interface InlineEditPluginState {
  mode: 'generating' | 'review' | null;
  range: InlineEditRange | null;
  decorations: DecorationSet;
}

type InlineEditMeta =
  | { type: 'generating'; from: number; to: number }
  | { type: 'review-highlight'; from: number; to: number }
  | { type: 'clear' };

const EMPTY_STATE: InlineEditPluginState = {
  mode: null,
  range: null,
  decorations: DecorationSet.empty,
};

const clampRange = (doc: ProseMirrorNode, from: number, to: number): InlineEditRange => {
  const max = doc.content.size;
  const clampedFrom = Math.max(0, Math.min(from, max));
  return { from: clampedFrom, to: Math.max(clampedFrom, Math.min(to, max)) };
};

export const createInlineSelectionEditPlugin = () =>
  new Plugin<InlineEditPluginState>({
    key: inlineSelectionEditPluginKey,
    state: {
      init: () => EMPTY_STATE,
      apply(tr, pluginState) {
        const meta = tr.getMeta(inlineSelectionEditPluginKey) as
          | InlineEditMeta
          | undefined;

        if (meta) {
          if (meta.type === 'clear') return EMPTY_STATE;
          const range = clampRange(tr.doc, meta.from, meta.to);
          if (range.from === range.to) return EMPTY_STATE;
          if (meta.type === 'generating') {
            return {
              mode: 'generating',
              range,
              decorations: DecorationSet.create(tr.doc, [
                Decoration.inline(range.from, range.to, {
                  class: `${SELECTION_CONTEXT_HIGHLIGHT_CLASS} ${INLINE_EDIT_PENDING_CLASS}`,
                }),
              ]),
            };
          }
          return {
            mode: 'review',
            range,
            decorations: DecorationSet.create(tr.doc, [
              Decoration.inline(range.from, range.to, {
                class: SELECTION_CONTEXT_HIGHLIGHT_CLASS,
              }),
            ]),
          };
        }

        if (!pluginState.mode || !tr.docChanged) return pluginState;

        const mappedFrom = tr.mapping.map(pluginState.range?.from ?? 0, 1);
        const mappedTo = tr.mapping.map(pluginState.range?.to ?? 0, -1);
        return {
          ...pluginState,
          range: clampRange(tr.doc, mappedFrom, mappedTo),
          decorations: pluginState.decorations.map(tr.mapping, tr.doc),
        };
      },
    },
    props: {
      decorations(state) {
        return this.getState(state)?.decorations ?? DecorationSet.empty;
      },
    },
  });

/** Current plugin state for an editor (null when the plugin isn't registered). */
export const getInlineEditPluginState = (editor: Editor): InlineEditPluginState | null =>
  inlineSelectionEditPluginKey.getState(editor.state) ?? null;

/** True when an inline AI edit targets content inside the given section node. */
export const isInlineSelectionEditWithinSection = (
  editor: Editor,
  sectionPos: number,
  sectionNodeSize: number
): boolean => {
  const pluginState = getInlineEditPluginState(editor);
  if (!pluginState?.mode || !pluginState.range) return false;

  const { from, to } = pluginState.range;
  return from > sectionPos && to < sectionPos + sectionNodeSize;
};

export const dispatchInlineEditMeta = (editor: Editor, meta: InlineEditMeta): void => {
  if (!isEditorLive(editor, 'inline-edit-decorations')) return;
  editor.view.dispatch(editor.state.tr.setMeta(inlineSelectionEditPluginKey, meta));
};
